import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { VegobjekterResult } from '@/domain/vegobjekter/VegobjekterResult';
import InfoBox from '@/components/common/InfoBox';
import { statePolygon, statePreferences } from '@/selectors/selectors';
import { formatNumber } from '@/utils/Utils';
import * as React from 'react';
import { connect } from 'react-redux';
import { useSelector } from 'react-redux';

interface HitStatisticsProps {
    vegobjekttype: Vegobjekttype;
    result: VegobjekterResult;
}

function format(number: number) {
    if (number === null || number === undefined) return null;

    return formatNumber(Math.round(number));
}

const PolygonStatisticsInfo = ({ inline = false }: { inline?: boolean }) => {
    const hasPolygon = useSelector(statePolygon) != null;

    if (!hasPolygon) {
        return null;
    }

    return (
        <InfoBox
            className={`info-box-container--statistics${inline ? ' info-box-container--statistics-inline' : ''}`}
            triggerAriaLabel="Vis forklaring av polygonstatistikk"
        >
            <p>Statistikk for polygonsøk på antall og lengde er et overslag.</p>
            <p>
                I beregningen bruker vi et rektangel som omslutter polygonet, og et
                tilsvarende rektangel rundt hvert vegobjekt.
            </p>
            <p>
                Da kan noen objekter nær kanten bli telt med selv om de ikke egentlig
                ligger innenfor polygonet. Derfor kan antallet bli noe høyere enn det
                faktiske tallet.
            </p>
        </InfoBox>
    );
};

export const AdvancedFilterWarningIcon = ({ inline = false }: { inline?: boolean }) => {
    const className = inline
        ? 'c-filtere__section-warning-indicator c-filtere__section-warning-indicator--inline'
        : 'c-filtere__section-warning-indicator';

    return (
        <span
            className={className}
            title="Filteret er ugyldig, bruker sist anvendte"
            aria-label="Filteret er ugyldig, bruker sist anvendte"
            role="img"
        >
            <i
                className="icon-exclamation-triangle c-filtere__section-warning-icon"
                aria-hidden="true"
            />
        </span>
    );
};

const HitStatisticsComponent = ({ vegobjekttype, result }: HitStatisticsProps) => {
    const preferences = useSelector(statePreferences);
    const { totalStatistics, extentStatistics, vegobjekter, areaStatistics } = result;

    if (extentStatistics == null) {
        return <span className="c-hva-info__statistics">Ingen treff</span>;
    }

    const showLength = vegobjekttype.isLine() && preferences.fetchStatisticsLength;

    // No Omrade in search, or omrader enclosed by extent.
    if (totalStatistics == null) {
        const extentAntall = extentStatistics.antall;
        const hasResults =
            (extentAntall >= 0 && vegobjekter.length > 0) || areaStatistics.length > 0;

        if (!hasResults) {
            return (
                <span className="c-hva-info__statistics">{`${format(
                    extentAntall
                )} vegobjekter (Utenfor kartvindu)`}</span>
            );
        }

        return (
            <>
                <span className="c-hva-info__statistics">{`${format(extentAntall)} vegobjekter`}</span>
                {showLength && extentStatistics.lengde !== undefined && (
                    <span className="c-hva-info__statistics c-hva-info__statistics--right">
                        {`${format(extentStatistics.lengde)} meter`}
                    </span>
                )}
            </>
        );
    }

    if (!(totalStatistics.antall > 0)) {
        return <span className="c-hva-info__statistics">Ingen treff</span>;
    }

    // Omrade in search, add supplementary statistics.
    const extentCount = format(extentStatistics.antall) ?? '0';
    const totalCount = format(totalStatistics.antall);
    const totalStretch = format(totalStatistics.lengde);
    const extentStretch = format(extentStatistics.lengde);
    return (
        <>
            <div className="c-hva-info__statistics-block">
                <div className="c-hva-info__statistics-row">
                    <span className="c-hva-info__statistics">{`${totalCount} vegobjekter`}</span>
                    {showLength && totalStretch && (
                        <span className="c-hva-info__statistics c-hva-info__statistics--right">
                            {`${totalStretch} meter`}
                        </span>
                    )}
                </div>
                {totalCount !== extentCount && (
                    <div className="c-hva-info__statistics-row">
                        <span className="c-hva-info__suplementary-statistics">
                            {`${extentCount} vises i kartutsnittet`}
                        </span>
                        {showLength && extentStretch && (
                            <span className="c-hva-info__suplementary-statistics c-hva-info__suplementary-statistics--right">
                                {`${extentStretch} meter`}
                            </span>
                        )}
                    </div>
                )}
            </div>
            <PolygonStatisticsInfo inline />
        </>
    );
};

export const HitStatistics = connect(null)(HitStatisticsComponent);

interface AllCategoriesHitStatisticsProps {
    vegobjekttype: Vegobjekttype;
    result: VegobjekterResult;
    showAdvancedFilterWarning?: boolean;
}

const AllCategoriesHitStatisticsComponent = ({
    vegobjekttype,
    result,
    showAdvancedFilterWarning = false,
}: AllCategoriesHitStatisticsProps) => {
    const preferences = useSelector(statePreferences);
    const hasPolygon = useSelector(statePolygon) != null;

    const antall = result.totalStatistics?.antall ?? result.extentStatistics?.antall;
    const lengde = result.totalStatistics?.lengde ?? result.extentStatistics?.lengde;
    const showLength =
        vegobjekttype.isLine() && lengde != null && preferences.fetchStatisticsLength;
    const hasTrailingContent = showLength || showAdvancedFilterWarning || hasPolygon;

    if (antall == null) {
        return <span className="c-hva-info__statistics">Ingen treff</span>;
    }

    return (
        <div className="c-hva-info__statistics-row c-hva-info__statistics-row--all-categories">
            <span className="c-hva-info__statistics-label">Totalt:</span>
            <span className="c-hva-info__statistics">{`${format(antall)} vegobjekter`}</span>
            {hasTrailingContent && (
                <div className="c-hva-info__statistics-trailing">
                    {showLength && (
                        <span className="c-hva-info__statistics c-hva-info__statistics--right">{`${format(
                            lengde
                        )} meter`}</span>
                    )}
                    {showAdvancedFilterWarning && <AdvancedFilterWarningIcon inline />}
                    {hasPolygon && <PolygonStatisticsInfo />}
                </div>
            )}
        </div>
    );
};

export const AllCategoriesHitStatistics = connect(null)(
    AllCategoriesHitStatisticsComponent
);
